import { usePresence } from '@common/hooks/usePresence';
import { Flex, Separator } from '@radix-ui/themes';
import { accentColors } from '@radix-ui/themes/props';
import { useMemo } from 'react';
import { ActionButton } from '../../../common/components/ActionButton';
import { UseSelectionResult } from '../hooks';

import styles from './SelectionRow.module.css';

type Color = (typeof accentColors)[number];
export interface BulkAction<T> {
    label: string;
    onClick: (items: T[]) => void;
    color?: Color;
    icon?: React.ReactNode;
}

interface TableBulkActionsProps<TData extends { id: string }> {
    selectionState: UseSelectionResult<TData>;
    actions: BulkAction<TData>[];
}

export const TableBulkActions = <TData extends { id: string }>({
    selectionState,
    actions,
}: TableBulkActionsProps<TData>) => {
    const selectedItems = useMemo(() => {
        return Object.values(selectionState.selectedItems).filter(
            (value): value is TData =>
                typeof value === 'object' && value !== null,
        );
    }, [selectionState.selectedItems]);

    const { shouldRender, presenceProps } = usePresence(
        selectedItems.length > 0,
        500,
    );

    if (!shouldRender) return null;
    return (
        <Flex gap={'2'} className={styles.animatedContainer} {...presenceProps}>
            <Separator mr={'1'} orientation={'vertical'} size={'1'} />

            {actions.map(({ label, icon, color, onClick }) => (
                <ActionButton
                    key={label}
                    color={color}
                    onClick={() => onClick(selectedItems)}
                >
                    {icon}
                    {label}
                </ActionButton>
            ))}
        </Flex>
    );
};
